<template>
    <v-card class="full-card overflow-y-auto" elevation="3">
        <v-card-text class="py-0 pt-3 text-start">
            <v-text-field
                v-model="filters['quick_search']['data']"
                append-inner-icon="mdi-magnify"
                density="compact"
                hide-details
                label="Quick Search"
                single-line
                variant="solo"
                @keydown.enter="fetchDevices"
                @click:append-inner="fetchDevices"
            />
            <v-select
                density="compact"
                label="Sort By"
                variant="solo"
                v-model="filters['sort']['data']"
                :items="sortTypes"
                class="pt-3"
            >
                <template v-slot:item="{ props, item }">
                    <v-list-item
                        :append-icon="sortingIcon(item.value)"
                        v-bind="props"
                        @click="updateSorting(item.value)"/>
                </template>
            </v-select>

            <!-- Accounts for the trackers this list no longer shows, and routes
                 to the screen that can bring them back. Nothing hidden, nothing
                 to explain - no chip. -->
            <div v-if="hiddenCount" class="px-1 pb-1">
                <v-chip color="grey" prepend-icon="mdi-map-marker-off" rounded="lg"
                        size="x-small" variant="tonal" :to="'/devices'">
                    {{ hiddenCount }} hidden from this screen
                </v-chip>
            </div>

            <div class="d-flex justify-space-between align-center">
                <v-btn variant="text" size="small" @click="filtersDialogSwitch = true">
                    <v-icon start icon="mdi-tune-variant" size="18"/>
                    Filters
                    <v-badge v-if="Object.keys(activeFilters).filter(k => !['sort','order'].includes(k)).length"
                             :content="Object.keys(activeFilters).filter(k => !['sort','order'].includes(k)).length"
                             color="primary" inline class="ml-1"/>
                </v-btn>
                <v-btn v-if="Object.keys(activeFilters).filter(k => !['sort','order'].includes(k)).length"
                       variant="text" size="x-small" color="grey" @click="clearFilters">
                    Clear all
                </v-btn>
                <v-spacer/>
                <v-btn
                    :icon="`mdi-view-${detailedView?'stream':'quilt'}`"
                    size="small" variant="text"
                    @click="switchView"
                />
            </div>
            <div v-if="Object.keys(activeFilters).filter(k => !['sort','order'].includes(k)).length"
                 class="d-flex flex-wrap ga-1 mb-2">
                <v-chip v-for="(filter, key) in activeFilters" :key="key"
                        v-show="!['sort','order'].includes(key)"
                        size="small" closable variant="tonal" color="primary"
                        @click:close="removeFilter(key)">
                    {{ filter.label }}
                </v-chip>
            </div>

            <v-list>
                <v-list-item v-for="(device,index) in visibleDevices" v-if="!processing && visibleDevices.length"
                             :key="device.id ?? device.slno"
                             :id="`d-${device.slno}`"
                             @click="selectDevice(device)"
                             :class="{
                                 'bg-grey-lighten-1':device.id === selectedDevice?.id,
                                 'bg-grey-lighten-5':(index%2),
                                 ['status-' + device.status_color]: true
                             }"
                             class="px-1 rounded-sm device-item"
                             @dblclick="menuAction('zoom')"
                             @contextmenu.prevent="(event) => showContextMenu(event,device)"
                >
                    <template v-slot:prepend>
                        <SubjectAvatar :device="device" :size="36" class="me-2"/>
                    </template>
                    <div v-if="detailedView">
                        <div class="d-flex justify-space-between">
                            <div>
                                <v-icon class="opacity-70" size="x-small" v-bind="signals(device.signal_bars??0)"/>
                                <v-icon class="opacity-70" size="x-small" v-bind="battery(device)"/>
                                <small v-if="batteryPct(device)" class="opacity-70 me-1" style="font-size: 10px">{{ batteryPct(device) }}</small>
                                <v-tooltip v-if="isLowBatteryDevice(device)" :text="lowBatteryHint(device)" location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" class="me-1" color="#d70000" size="x-small" icon="mdi-battery-alert-variant"/>
                                    </template>
                                </v-tooltip>
                                <v-tooltip v-if="obdStatus(device).show" :text="obdHint(device)" location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" class="me-1" color="#F97316" size="x-small" icon="mdi-engine"/>
                                    </template>
                                </v-tooltip>
                                <small class="font-weight-bold">
                                    {{ device.asset_label || device.slno || 'Tracker' }}
                                </small>
                            </div>
                            <div>
                                <v-chip
                                    v-if="device.motion_state && (device.gps_speed_raw ?? device.gps_speed) > 2"
                                    :text="`${strOperations().unitifySpeed(device)}`"
                                    class="mr-2"
                                    rounded="lg"
                                    size="x-small"
                                    variant="outlined"
                                />
                                <v-chip
                                    :style="{backgroundColor: statusChip(device, trustTick).color, color: '#fff'}"
                                    :text="statusChip(device, trustTick).label"
                                    rounded="lg"
                                    size="x-small"
                                    variant="flat"
                                />
                            </div>
                        </div>
                        <p>{{ device.slno }}</p>
                        <p v-if="presence(device, trustTick)" class="text-caption text-medium-emphasis mb-0">
                            {{ presence(device, trustTick) }}
                        </p>
                        <p class="text-caption">
                            <template v-if="device.current_zone">At {{ device.current_zone }}</template>
                            <template v-else-if="device.location || device.formatted_address">{{ device.location ?? device.formatted_address }}</template>
                            <template v-else-if="hasFix(device)"></template>
                            <template v-else>Location unavailable</template>
                        </p>
                        <div class="d-flex align-center text-caption text-medium-emphasis">
                            <span class="freshness-dot me-1"
                                  :style="{backgroundColor: freshnessColor(device, trustTick)}"
                                  :title="freshnessLabel(device, trustTick)"
                                  :aria-label="freshnessLabel(device, trustTick)"/>
                            Updated {{ updatedAgo(device, trustTick) }}
                        </div>
                        <v-chip
                            v-if="device.driver_first_name"
                            class="mr-2"
                            rounded="lg"
                            size="x-small"
                            variant="outlined"
                        >
                            <v-avatar class="me-2" size="18">
                                <v-img
                                    :lazy-src="avatarPlaceholder"
                                    :src="device.driver_photo"
                                    aspect-ratio="1/1" cover height="100%"/>
                            </v-avatar>
                            <small>{{ device.driver_first_name }} {{ device.driver_last_name }}</small>
                        </v-chip>
                    </div>
                    <p v-else>
                        <v-tooltip v-if="!device.asset_driver_id" text="No driver assigned">
                            <template v-slot:activator="{ props }">
                                <v-icon class="me-1"
                                        density="compact"
                                        icon="mdi-alert-circle-outline"
                                        size="x-small"
                                        v-bind="props"
                                />
                            </template>
                        </v-tooltip>
                        {{ device.slno + (device.asset_label ? ` (${device.asset_label})` : '') }}
                    </p>
                </v-list-item>
                <v-list-item v-else class="text-center">
                    <v-skeleton-loader v-if="processing" type="paragraph"/>
                    <p v-else-if="hiddenCount">Every tracker is hidden from this
                        screen.</p>
                    <p v-else>No device found</p>
                </v-list-item>
                <v-menu v-if="menu.state"
                        v-model="menu.state"
                        :target="menu.position"
                        density="compact"
                >
                    <v-list class="pa-0" density="compact">
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('copyLocation')">
                            {{ `${preSelectedDevice.lat}, ${preSelectedDevice.lng}` }}
                            <i class="mdi mdi-content-copy"></i>
                        </v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('findNearest')">Find Nearest Device
                        </v-list-item>
                        <v-list-item @click="menuAction('zoom')">Zoom To</v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('streetView')">Street View
                        </v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('direction')">Direction
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-list>
        </v-card-text>
    </v-card>

    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @applyFilters="fetchDevices"
        @resetFilters="clearFilters"
    />
</template>

<script setup>
import {computed, onBeforeMount, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {strOperations} from "@/composables/globals";
import {useClipboard} from "@/composables/clipboard";
import {batteryDisplay, isLowBatteryDevice, lowBatteryHint} from "@/composables/usePowerSourceIcon";
import {obdStatus, obdHint} from "@/composables/useObdStatus";
import {useSnackbarStore} from "@/stores/snackBarStore";
import avatarPlaceholder from "@/assets/images/avatar.png";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";
import SubjectAvatar from "@/components/SubjectAvatar.vue";
import {statusBadge, statusMeta, lastSeenAgo, freshness, freshnessLabel as freshnessWords, presenceText} from "@/composables/subjectKind";

// Re-render the time-based trust UI (status + "Updated Xm ago") every 30s even
// when no new data arrives, so a device that stops reporting visibly goes
// stale/offline on its own. The unused _tick param is passed trustTick from the
// template; reading it in the compiled render fn is what registers the reactive
// dependency (robust against prod minification stripping a bare `void ref.value`).
const trustTick = ref(0);
let trustTimer = null;

// Shared, accessible status badge (same source as the map marker + popup).
const statusChip = (device, _tick) => statusBadge(device);
// Colour + accessible label for the "Updated Xm ago" freshness dot.
const freshnessColor = (device, _tick) => statusMeta(freshness(device)).color;
const freshnessLabel = (device, _tick) => freshnessWords(device);
const updatedAgo = (device, _tick) => lastSeenAgo(device);
// "Moving for X" / "Here for X"; _tick forces the 5s re-eval like the others.
const presence = (device, _tick) => presenceText(device);

// True when the device has a real position. A fresh, moving device can have
// valid coords but no resolved address yet, so "Location unavailable" must be
// gated on coordinates - not the address string - or a live device is
// mislabelled. Mirrors DevicesMap.deviceLatLng / ShareView.hasPosition.
const hasFix = (device) => {
    // Number(): REST sends these Postgres numerics as strings, and
    // "0.00000000" === 0 is false, so the null-island sentinel used to read as a
    // real position here while the map correctly hid the marker.
    const lat = Number(device?.lat ?? device?.last_known_lat);
    const lng = Number(device?.lng ?? device?.last_known_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    return !(lat === 0 && lng === 0); // null-island guard
};

const detailedView = ref(1);
const processing = ref(true);
const snackbar = useSnackbarStore();
// Default view: Last Update, most-recently-reported first (state 1). Backend
// already sorts non-reporting trackers last (nulls-last), so stale devices sink.
const sortClickCount = ref(1);
// The field the toggle currently owns. Tracked separately from
// filters.sort.data because the v-select's own v-model also writes that on click
// (before updateSorting runs), which would make same-vs-new detection unreliable.
const activeSortField = ref('last_updated');
const selectedDevice = ref(null);
const preSelectedDevice = ref(null);
const {copyToClipboard} = useClipboard();
const emits = defineEmits(['loaded', 'selectDevice', 'zoomToDevice', 'nearestToDevice'])
const props = defineProps(['newDeviceSelection']);

const devices = ref([]);

// Hiding a tracker takes it off this screen entirely - map, list and all -
// rather than only removing its pin. That is the reading the setting has: the
// owner asked not to see it here. It keeps reporting, alerting and recording
// trips throughout, and the Devices screen still lists it, which is where the
// count chip above leads. Matches the mobile app, which made this change first.
//
// The unfiltered list is still what feeds the map and the MQTT subscriptions,
// so a hidden tracker stays subscribed and its data stays current.
const visibleDevices = computed(() => devices.value.filter((d) => !d?.hidden_on_map));
const hiddenCount = computed(() => devices.value.length - visibleDevices.value.length);
const headers = ref([]);
const sortTypes = [
    {title: 'Serial no.', value: 'slno'},
    {title: 'Asset', value: 'asset_label'},
    {title: 'Last Update', value: 'last_updated'},
];
const deviceStatuses = ref([
    {id: 0, title: 'Active'},
    {id: 1, title: 'Disabled'},
    {id: 2, title: 'Needs Attention'},
    {id: 3, title: 'Missing'}
]);
const menu = ref({state: 0});
const filtersDialogSwitch = ref(false);
const filters = reactive({
    sort: {label: 'Sort by', data: 'last_updated', type: 'hidden'},
    order: {label: 'Order by', data: 'desc', type: 'hidden'},
    quick_search: {label: 'Quick Search', data: null, type: 'hidden'},
    label: {label: 'Tracker Name', data: null, icon: 'mdi-tag-outline', description: 'Search by name'},
    motion_state: {
        label: 'Activity', type: 'select', data: null, icon: 'mdi-run', description: 'Filter by movement', source: [
            {title: 'Idle', value: 0},
            {title: 'In Motion', value: 1},
        ]
    },
    device_status: {
        label: 'Status', type: 'select', data: null, icon: 'mdi-check-circle-outline', description: 'Filter by device status',
        title: 'title', value: 'id', source: deviceStatuses
    },
    geo_radius: {label: 'Nearby', type: 'number', data: null, latitude: null, longitude: null, icon: 'mdi-crosshairs-gps', description: 'Find trackers within a radius'},
    last_update: {label: 'Last Seen', data: null, action: null, type: 'datetime-local', icon: 'mdi-clock-outline', description: 'Filter by last report time'},
    serial_num: {label: 'Serial Number', data: null, action: null, icon: 'mdi-barcode', description: 'Search by device serial'},
});
const activeFilters = computed(() => {
    const activatedFilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter) {
            if (filter.data !== null) {
                activatedFilters[key] = filter;
            }
        }
    }
    return activatedFilters;
});

const resetSelectedDevice = () => {
    selectedDevice.value = null;
    emits('selectDevice', null);
}
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const battery = (device) => {
    const {icon, color} = batteryDisplay(device);
    return {icon, color};
}
// "58%" for phone devices, null for hardware (which shows only the bars icon).
const batteryPct = (device) => batteryDisplay(device).label;
const sortingIcon = (val) => {
    if (activeSortField.value === val) {
        switch (sortClickCount.value) {
            case 1:
                return 'mdi-sort-ascending';
            case 2:
                return 'mdi-sort-descending';
            default:
                return 'mdi-sort';
        }
    }
    return 'mdi-sort';
}
// Backend order param for a field at a given cycle state. State 1 is the "first
// press" direction and state 2 its opposite. Last Update reads best recent-first,
// so its state 1 is newest-first (order=desc); every other field starts ascending.
const orderFor = (field, state) => {
    if (field === 'last_updated') {
        return state === 1 ? 'desc' : 'asc';
    }
    return state === 1 ? 'asc' : 'desc';
}
// Sort toggle. Deterministic so picking or re-tapping a field always lands on a
// predictable direction (the old version leaned on the v-select's own selection
// event to reset the counter, which raced this handler and scrambled the order).
// State machine per field: 1 = first direction, 2 = opposite, 0 = off (unsorted).
// Re-tapping the same field advances 1 -> 2 -> off -> 1 ...
const updateSorting = (val) => {
    if (activeSortField.value !== val) {
        // A different field was picked: start its cycle at the first direction.
        sortClickCount.value = 1;
        activeSortField.value = val;
    } else {
        // Same field re-tapped: advance first -> opposite -> off -> first ...
        sortClickCount.value = (sortClickCount.value + 1) % 3;
    }

    if (sortClickCount.value === 0) {
        activeSortField.value = null;
        filters['sort']['data'] = null;
        filters['order']['data'] = null;
    } else {
        filters['sort']['data'] = val;
        filters['order']['data'] = orderFor(val, sortClickCount.value);
    }
    fetchDevices();
}
const selectDevice = (device) => {
    selectedDevice.value = device;
    emits('selectDevice', device);
}
const showContextMenu = (e, device) => {
    e.preventDefault();
    menu.value = {
        state: true,
        position: [e.clientX, e.clientY]
    };
    preSelectedDevice.value = device;
}
const menuAction = (type) => {
    if (preSelectedDevice.value) {
        switch (type) {
            case 'copyLocation':
                copyToClipboard(`${preSelectedDevice.value.lat}, ${preSelectedDevice.value.lng}`);
                break;
            case 'findNearest':
                emits('nearestToDevice', preSelectedDevice.value)
                break;
            case 'zoom':
                emits('zoomToDevice', preSelectedDevice.value)
                break;
            case 'direction':
                window.open(`http://maps.google.com/maps/dir//${preSelectedDevice.value.lat},${preSelectedDevice.value.lng}`,
                    "_blank");
                break;
            case 'streetView':
                window.open(`http://maps.google.com/maps?q=${preSelectedDevice.value.lat},${preSelectedDevice.value.lng}&layer=t&cbll=${preSelectedDevice.value.lat},${preSelectedDevice.value.lng}&cbp=12,0,0,0,0&output=classic`,
                    "_blank");
                break;
        }
    }
}
const switchView = () => {
    detailedView.value = !detailedView.value;
}
const removeFilter = (key) => {
    filters[key]['data'] = null;
    filters[key]['action'] ? filters[key]['action'] = null : '';
    if (Object.keys(activeFilters.value).length) {
        fetchDevices();
    } else {
        clearFilters();
    }
}
const clearFilters = (callApi = true) => {
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    if (callApi) {
        fetchDevices();
    }
}
// Retry a failed device load with backoff so a transient blip doesn't leave the
// map empty with no recovery (nothing else re-fetches after a failed initial
// load). A fresh call cancels any pending retry.
let deviceRetryTimer = null;
let deviceListUnmounted = false;
const MAX_DEVICE_RETRIES = 4;
const fetchDevices = (attempt = 0) => {
    // Some callers are bound directly to template events and pass an Event/
    // undefined - coerce to a fresh load so retry still applies for them.
    attempt = Number.isInteger(attempt) ? attempt : 0;
    clearTimeout(deviceRetryTimer);
    devices.value = [];
    processing.value = true;
    let filtered = Object.values(activeFilters.value).length;
    resetSelectedDevice();
    // approximate=1: include a just-activated device's seed position (flagged
    // approximate) so the map can draw it faded instead of hiding it. GET passes
    // it as a query param; the filtered POST keeps filters as the body and
    // carries the flag in the URL.
    send(`${filtered ? 'post' : 'get'}`,
        `personal-trackers${filtered ? '/filtered?approximate=1' : ''}`,
        filtered ? filters : {approximate: 1})
        .then((response) => {
            devices.value = response.data.data.devices;
            headers.value = response.data.data.headers;
            emits('loaded', {devices: devices.value, headers: headers.value})
        })
        .catch((error) => {
            if (deviceListUnmounted) return; // don't re-arm a retry / snackbar after teardown
            // Retry silently a few times; only surface the error once we give up.
            if (attempt < MAX_DEVICE_RETRIES) {
                deviceRetryTimer = setTimeout(
                    () => fetchDevices(attempt + 1),
                    Math.min(15000, 2000 * (attempt + 1)));
            } else {
                snackbar.showSnackbar('Devices could not be loaded', 'error');
            }
        }).finally(() => processing.value = false);
};

watch(() => props.newDeviceSelection, (newDevice) => {
    if (newDevice && selectedDevice.value?.id !== newDevice.id) {
        selectedDevice.value = newDevice;
        // The row may legitimately not exist: a hidden tracker has none, and a
        // selection can also arrive while the list is mid-refetch. Unguarded
        // this threw and took the selection with it.
        document.getElementById(`d-${newDevice.slno}`)
            ?.scrollIntoView({behavior: 'smooth'});
    }
})

const onMqttTokenRefresh = () => fetchDevices();
// Debounce reloads so a burst of saves in the circle coalesces into one refetch.
let reloadTimer = null;
const onCompanyDataReload = () => {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => fetchDevices(), 800);
};
onBeforeMount(() => {
    fetchDevices()
})
onMounted(() => {
    window.addEventListener('mqtt-token-refresh', onMqttTokenRefresh);
    window.addEventListener('company-data-reload', onCompanyDataReload);
    trustTimer = setInterval(() => trustTick.value++, 30000);
})
onUnmounted(() => {
    deviceListUnmounted = true;
    window.removeEventListener('mqtt-token-refresh', onMqttTokenRefresh);
    window.removeEventListener('company-data-reload', onCompanyDataReload);
    clearTimeout(reloadTimer);
    clearTimeout(deviceRetryTimer);
    if (trustTimer) clearInterval(trustTimer);
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    if (localStorageProfiles?.selectedProfile) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            selectedProfile: null,
            profiles: localStorageProfiles.profiles,
        }));
    }
})
</script>

<style>
.device-item {
    border-width: 1px !important;
    border-bottom: 1px solid rgb(0 0 0 / 12%);
}

.freshness-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: 0 0 auto;
}

.device-item:hover {
    background-color: #dcdcdc;
}

.cursor-pointer .v-field:hover, .cursor-pointer input:hover {
    cursor: pointer !important;
}

.status-blue {
    border-bottom-color: rgba(33, 117, 243, 0.6) !important;
}

.status-red {
    border-bottom-color: rgba(215, 0, 0, 0.6) !important;
}

.status-amber {
    border-bottom-color: rgba(255, 158, 1, 0.6) !important;
}
</style>
